import Mexp from 'math-expression-evaluator';

export type TemplateParams =
  | { value: number; style: 'currency'; currency?: string }
  | { value: number; style: 'decimal' }
  | { value: Date; style: 'date' | 'datetime' }
  | { value: string; style: 'text' };

export type VariableConfig = {
  variable: string;
  style: 'currency' | 'decimal' | 'text' | 'date' | 'datetime';
};

export const buildTemplateString = (params: TemplateParams): string => {
  const { value, style } = params;

  // Early return for null/undefined values to prevent rendering issues
  if (value === null || value === undefined) {
    return '--';
  }

  try {
    switch (style) {
      case 'currency': {
        // Ensure value is a valid number for currency formatting
        const numValue = typeof value === 'number' ? value : Number(value);
        if (isNaN(numValue)) {
          return '--';
        }
        return getCurrencyString(numValue, params.currency);
      }
      case 'date': {
        // Validate date before formatting to prevent rendering issues
        const dateValue = value instanceof Date ? value : new Date(value);
        if (isNaN(dateValue.getTime())) {
          return '--';
        }
        return new Intl.DateTimeFormat('en-US').format(dateValue);
      }
      case 'datetime': {
        // Validate date before formatting to prevent rendering issues
        const dateValue = value instanceof Date ? value : new Date(value);
        if (isNaN(dateValue.getTime())) {
          return '--';
        }
        return new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          timeZoneName: 'short',
        }).format(dateValue);
      }
      case 'decimal': {
        // Ensure value is a valid number for decimal formatting
        const numValue = typeof value === 'number' ? value : Number(value);
        if (isNaN(numValue)) {
          return '--';
        }
        return new Intl.NumberFormat('en-US', { style }).format(numValue);
      }
      default:
        return String(value);
    }
  } catch (e) {
    // Log error but don't let it break rendering
    console.warn('Error in buildTemplateString:', e, { value, style });
    return '--';
  }
};

export const getCurrencyString = (value: number, currency?: string, forceWholeNumber?: boolean) => {
  try {
    // Validate input
    if (typeof value !== 'number' || isNaN(value)) {
      return '--';
    }
    // Rounding Error removal for javascript
    const roundedValue = Math.round(value * 100) / 100;
    const isWholeNumber = Number.isInteger(roundedValue);
    const maximumFractionDigits = forceWholeNumber ? 0 : 2;
    const minimumFractionDigits = forceWholeNumber ? 0 : isWholeNumber ? 0 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency ?? 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits,
      maximumFractionDigits,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).format(roundedValue);
  } catch (e) {
    console.warn('Error in getCurrencyString:', e, { value, currency });
    return '--';
  }
};

export const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  return (email ?? '??').slice(0, 2).toUpperCase();
};

const mexp = new Mexp();

export const evalFormula = (formula: string) => {
  const expressionWithoutSpaces = formula.replace(/ /g, '');
  return Math.round(mexp.eval(expressionWithoutSpaces));
};
